import { DeleteOutlined, FilterOutlined, MoreOutlined, RobotFilled, SyncOutlined } from "@ant-design/icons";
import { TerminalOutlined } from "@mui/icons-material";
import { ActionExecutor, type ActionExecutorHandler } from "@src/frontend/components/ActionExecutor";
import { usePermission } from "@src/frontend/hooks/usePermission";
import { useRestAPI } from "@src/frontend/hooks/useRestAPI";
import { useScriptOptions } from "@src/frontend/hooks/useScriptOptions";
import { useAgentStore } from "@src/frontend/stores/agentStore";
import type { AgentModel } from "@src/generated/prisma/models";
import { LogtoPermission } from "@src/utils/constants";
import { App, Button, Dropdown, Flex, Input, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/lib/table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

const api = '/api/agents';
const StyledContainer = styled(Flex)`
  .ant-typography-edit-content {
    position: relative;
    top: 0;
    left: 0;
  }
`;

export const AgentList = () => {
  const { modal: AppModal } = App.useApp();
  const [name, setName] = useState('');
  const { hasPermission } = usePermission();
  const { online: agentStates, setAgents } = useAgentStore(data => data);
  const { listReq: listAgentReq, updateReq: updateAgentReq, createReq: createAgentReq, delReq: deleteAgentReq } = useRestAPI<AgentModel>(api);
  useEffect(() => {
    setAgents(listAgentReq.data?.data || []);
  }, [listAgentReq.data?.data, setAgents]);
  const handleAdd = useCallback(async () => {
    const res = await createAgentReq.runAsync({ name });
    if (res.ok) {
      setName('');
    }
    await listAgentReq.runAsync();
  }, [name]);
  const adminColumns = useMemo<ColumnsType<AgentModel>>(() => {
    if (!hasPermission(LogtoPermission.ADMIN)) return [];
    const filters = new Set(listAgentReq.data?.data?.map(e => e.logto_uid)).values().map(e => ({ text: e, value: e })).toArray();
    return [
      { width: 220, dataIndex: 'logto_uid', align: 'center', title: 'Logto UID',
        filters,
        ellipsis: true,
        filterIcon: <FilterOutlined />,
        onFilter: (value, record) => record.logto_uid === value,
      },
    ];
  }, [hasPermission, listAgentReq]);
  const executor = useRef<ActionExecutorHandler>(null);
  const items = useScriptOptions();
  return (
    <StyledContainer vertical gap={12}>
      <Typography.Title level={3}>
        <Flex gap={8} align="center">
          <span>代理</span>
          <Button onClick={listAgentReq.runAsync}><SyncOutlined /></Button>
        </Flex>
      </Typography.Title>
      <Flex gap={12} align="center">
        <Input onPressEnter={handleAdd} placeholder="我的 PC" prefix="名称：" value={name} onChange={e => setName(e.target.value)} />
        <Button loading={createAgentReq.loading} disabled={!name} onClick={handleAdd} icon={<RobotFilled />}>创建代理</Button>   
      </Flex>
      <Table
        rowKey={e => e.id}
        size="small"
        dataSource={listAgentReq.data?.data}
        scroll={{ x: 600 }}
        columns={[
          { width: 360, dataIndex: 'name', title: '执行节点', render: (v, { id }) => {
            return (
              <Flex vertical gap={2}>
                <Typography.Text strong editable={{ async onChange(value) {
                  if(!value || value === v) return;
                  await updateAgentReq.runAsync({ name: value }, id);
                  await listAgentReq.runAsync();
                } }}>{v}</Typography.Text>
                <Typography.Text type="secondary" copyable={{ text: id }} ellipsis>{id}</Typography.Text>
              </Flex>
            );
          } },
          ...adminColumns,
          { width: 120, dataIndex: 'id', title: '状态', render: v => {
            const online = agentStates?.[v];
            return online
              ? <Tag color={'success'}>在线</Tag>
              : <Tag color={'error'}>离线</Tag>;
          } },
          { fixed: 'right', width: 210, align: 'right', dataIndex: 'id', title: '操作', render: (id, record) => {
            return (
              <Flex gap={12}>
                <Dropdown
                  disabled={!agentStates?.[id]}
                  menu={{
                    items,
                    onClick: (e) => {
                      executor.current?.open({
                        agent_id: id,
                        script_id: e.key,
                      });
                    },
                  }}
                >
                  <Button
                    icon={<TerminalOutlined />}
                    onClick={() => {
                      executor.current?.open({ agent_id: id });
                    }}
                  >
                    执行动作
                  </Button>
                </Dropdown>
                <Dropdown menu={{ items: [{ key: 'delete', danger: true, icon: <DeleteOutlined />, label: '删除节点' }], onClick: ({ key }) => {
                  if (key !== 'delete') return;
                  AppModal.confirm({ title: `删除节点「${record.name}」？`, content: '关联会话将解除绑定。', okText: '删除', okButtonProps: { danger: true }, onOk: async () => { await deleteAgentReq.runAsync(id); await listAgentReq.runAsync(); } });
                } }}><Button icon={<MoreOutlined />} /></Dropdown>
              </Flex>
            );
          } }
        ]}
      />
      <ActionExecutor ref={executor} />
    </StyledContainer>
  );
}
